import React, { useState, useEffect } from 'react';
import { propertiesApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import {
  Building2,
  Plus,
  Search,
  Star,
  MapPin,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';

const propertyTypes = [
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'RESORT', label: 'Resort' },
  { value: 'BOUTIQUE', label: 'Boutique' },
  { value: 'SERVICED_APT', label: 'Serviced Apartment' },
];

const statusColors = {
  SETUP: { bg: 'bg-warning/10', text: 'text-warning', icon: Clock },
  ACTIVE: { bg: 'bg-success/10', text: 'text-success', icon: CheckCircle2 },
  INACTIVE: { bg: 'bg-muted', text: 'text-muted-foreground', icon: XCircle },
  CLOSED: { bg: 'bg-destructive/10', text: 'text-destructive', icon: XCircle },
};

export default function PropertiesPage() {
  const { hasPermission, currentProperty, switchProperty } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_country: 'ET',
    property_type: 'HOTEL',
    star_rating: '',
  });

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await propertiesApi.list();
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        ...formData,
        star_rating: formData.star_rating ? parseInt(formData.star_rating) : null,
      };
      await propertiesApi.create(data);
      setIsDialogOpen(false);
      setFormData({
        code: '',
        name: '',
        address_street: '',
        address_city: '',
        address_state: '',
        address_country: 'ET',
        property_type: 'HOTEL',
        star_rating: '',
      });
      loadProperties();
    } catch (error) {
      console.error('Failed to create property:', error);
      alert(error.response?.data?.detail || 'Failed to create property');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (propertyId) => {
    try {
      await propertiesApi.activate(propertyId);
      loadProperties();
    } catch (error) {
      console.error('Failed to activate property:', error);
      alert(error.response?.data?.detail || 'Failed to activate property');
    }
  };

  const handleModeChange = async (property, newType) => {
    try {
      await propertiesApi.update(property.id, { business_type: newType });
      // Refresh the active property so the menu switches immediately
      if (currentProperty?.id === property.id) {
        switchProperty({ ...currentProperty, business_type: newType });
      }
      loadProperties();
    } catch (error) {
      console.error('Failed to update mode:', error);
      alert(error.response?.data?.detail || 'Failed to update operating mode');
    }
  };

  const filteredProperties = properties.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.code.toLowerCase().includes(search.toLowerCase()) ||
      p.address_city?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-admin-heading)' }}>
            Properties
          </h1>
          <p className="text-muted-foreground">Manage your hotel properties</p>
        </div>

        {hasPermission('property:create') && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-property-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Property</DialogTitle>
                <DialogDescription>Create a new hotel property</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Code</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="HTL001"
                      required
                      maxLength={10}
                      data-testid="property-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type">Type</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                    >
                      <SelectTrigger data-testid="property-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Property Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Grand Hotel"
                    required
                    data-testid="property-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="street">Street Address</Label>
                  <Input
                    id="street"
                    value={formData.address_street}
                    onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.address_city}
                      onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                      placeholder="Addis Ababa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rating">Star Rating</Label>
                    <Select
                      value={formData.star_rating}
                      onValueChange={(value) => setFormData({ ...formData, star_rating: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <SelectItem key={rating} value={rating.toString()}>
                            {rating} Star{rating > 1 ? 's' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="save-property-button">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Create Property'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search properties..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="search-input"
        />
      </div>

      {/* Properties Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-5 w-32 bg-muted rounded" />
                <div className="h-4 w-20 bg-muted rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-4 w-full bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredProperties.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No properties found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProperties.map((property) => {
            const StatusIcon = statusColors[property.status]?.icon || Clock;
            return (
              <Card key={property.id} className="hover:shadow-md transition-shadow" data-testid={`property-card-${property.code}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{property.name}</CardTitle>
                      <CardDescription className="font-mono text-xs">
                        {property.code}
                      </CardDescription>
                    </div>
                    <div
                      className={cn(
                        'px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1',
                        statusColors[property.status]?.bg,
                        statusColors[property.status]?.text
                      )}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {property.status}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{property.address_city || 'No address'}, {property.address_country}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {property.star_rating ? (
                        Array.from({ length: property.star_rating }).map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">No rating</span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground capitalize">
                      {property.property_type?.toLowerCase().replace('_', ' ')}
                    </span>
                  </div>

                  {/* Operating mode (Hotel vs Training Center) */}
                  <div className="flex items-center justify-between gap-2 pt-1 border-t">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Mode</span>
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-[10px] font-semibold',
                        property.business_type === 'TRAINING_CENTER'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      )}>
                        {property.business_type === 'TRAINING_CENTER' ? 'Training Center' : 'Hotel'}
                      </span>
                    </div>
                    {hasPermission('property:update') && (
                      <Select
                        value={property.business_type || 'HOTEL'}
                        onValueChange={(v) => handleModeChange(property, v)}
                      >
                        <SelectTrigger className="h-7 w-[140px] text-xs" data-testid={`mode-select-${property.code}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HOTEL">Hotel</SelectItem>
                          <SelectItem value="TRAINING_CENTER">Training Center</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {property.status === 'SETUP' && hasPermission('property:admin') && (
                    <Button
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => handleActivate(property.id)}
                      data-testid={`activate-${property.code}`}
                    >
                      Activate Property
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
